<template>
  <v-expansion-panel class="reviewable-extraction-field">
    <extraction-field-header
      :required="field.required"
      :complete="complete"
      :open="open"
      :error="error"
    >
      {{ field.title }}
    </extraction-field-header>
    <v-divider class="divider" />
    <v-expansion-panel-content
      class="reviewable-extraction-field__content"
    >
      <v-item-group v-model="selectedModel" :multiple="isArrayType">
        <v-item
          v-for="option in availableOptions"
          :key="option.stringValue"
          v-slot="{ active, toggle }"
          :value="option.stringValue"
        >
          <div>
            <v-divider v-if="option.index !== 0" class="divider" />

            <selectable-extraction-value
              :value="option.stringValue"
              :selected="active"
              :researchers="option.researchers"
              :complete="isComplete(active, option.stringValue)"
              :hide-confirm="isArrayType"
              @click="onToggle(toggle)"
              @confirm="confirmSelection"
            />
          </div>
        </v-item>
        <v-divider v-if="hasOtherOption" class="divider" />
        <v-item
          v-if="hasOtherOption"
          v-slot="{ active, toggle }"
          value="__other"
        >
          <div>
            <selectable-extraction-value
              v-model="otherValue"
              :selected="active"
              :complete="isComplete(active, otherValue)"
              :hide-confirm="isArrayType"
              editable
              @click="onOtherSelected(toggle)"
              @confirm="confirmSelection"
            />
          </div>
        </v-item>
        <v-divider v-if="isArrayType" class="divider" />

        <v-row
          v-if="isArrayType"
          no-gutters
          justify="space-between"
          align="center"
          class="reviewable-extraction-field__confirm-row userselect-none px-4 white"
        >
          <div class="mr-2 subtitle-2 grey--text text--darken-2">
            {{ `${selectedModel.length} item(s) selected` }}
          </div>
          <confirm-consensus-chip
            :complete="currentSelectionComplete"
            @click="confirmSelection"
          />
        </v-row>
        <div v-if="showEmptyState" class="px-4">-</div>
      </v-item-group>
    </v-expansion-panel-content>
    <v-divider v-if="open" class="divider" />
  </v-expansion-panel>
</template>

<script>
import ExtractionFieldHeader from '@/components/DataExtraction/ExtractionFieldHeader';
import ConfirmConsensusChip from '@/components/DataExtraction/ConfirmConsensusChip';
import SelectableExtractionValue from '@/components/DataExtraction/SelectableExtractionValue';
import {
  DataExtractionFieldBase,
  DataExtractionFieldMultipleSelect,
  DataExtractionFieldText,
  DataExtractionReport,
  DataExtractionValueBase,
} from '@/models';
import { typeHelpers } from '@/modules';

export default {
  name: 'ReviewableExtractionField',
  components: {
    ConfirmConsensusChip,
    ExtractionFieldHeader,
    SelectableExtractionValue,
  },
  props: {
    value: {
      type: [String, Array],
      default: null,
      validator: (x) =>
        typeof x === 'string' ||
        typeHelpers.isArrayOfType(x, 'string'),
    },
    field: {
      type: [DataExtractionFieldBase],
      required: true,
    },
    extractionValues: {
      type: Array,
      default: () => [],
      validator: (field) =>
        typeHelpers.isArrayOfType(field, DataExtractionValueBase),
    },
    reports: {
      type: Array,
      default: () => [],
      validator: (x) =>
        typeHelpers.isArrayOfType(x, DataExtractionReport),
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    complete: {
      type: Boolean,
      default: false,
    },
    open: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input'],
  data: () => ({ selectedModel: '', otherValue: '' }),
  computed: {
    showEmptyState() {
      return (
        this.availableOptions.length === 0 && !this.hasOtherOption
      );
    },
    hasOtherOption() {
      if (this.readonly) {
        return false;
      }
      return (
        this.field instanceof DataExtractionFieldText ||
        this.field.otherOption
      );
    },
    otherConflictsExistingOptions() {
      return this.availableOptions.some(
        (option) => option.stringValue === this.otherValue
      );
    },
    valueToReportMap() {
      const map = {};
      for (const extractionValue of this.extractionValues) {
        const valueId = extractionValue.id;
        map[valueId] = this.reports.find((report) =>
          report.values.some(
            (reportValue) => reportValue.id === valueId
          )
        );
      }
      return map;
    },
    isArrayType() {
      return this.field instanceof DataExtractionFieldMultipleSelect;
    },
    availableOptions() {
      const optionMap = {};
      let optionIndex = 0;
      const addOptionIfNew = (option) => {
        if (!(option in optionMap)) {
          optionMap[option] = {
            index: optionIndex++,
            stringValue: option,
            researchers: [],
          };
        }
      };

      const valueReportMap = this.valueToReportMap;
      for (const extractionValue of this.extractionValues) {
        const researcher =
          valueReportMap[extractionValue.id]?.reviewer?.researcher;
        const options = extractionValue.valueArr || [
          extractionValue.value,
        ];
        for (const option of options) {
          addOptionIfNew(option);
          if (researcher) {
            optionMap[option].researchers.push(researcher);
          }
        }
      }

      if (this.field.multipleChoiceOptions) {
        this.field.multipleChoiceOptions.forEach(addOptionIfNew);
      }

      if (this.value) {
        if (this.isArrayType) {
          this.value.forEach(addOptionIfNew);
        } else {
          addOptionIfNew(this.value);
        }
      }
      return Object.values(optionMap).sort(
        (a, b) => a.index - b.index
      );
    },
    selection() {
      let selection = this.selectedModel;
      if (this.isArrayType) {
        selection = selection
          .map((val) => (val === '__other' ? this.otherValue : val))
          .filter((str) => str.length > 0);
      } else if (selection === '__other') {
        selection = this.otherValue;
      }
      return selection;
    },
    currentSelectionComplete() {
      if (!this.value) {
        return false;
      }

      if (this.isArrayType) {
        return (
          this.selectedModel.every((a) => this.value.includes(a)) &&
          this.value.every((a) => this.selectedModel.includes(a))
        );
      }
      return this.value === this.selectedModel;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue === null) {
          if (this.isArrayType) {
            newValue = [];
          } else {
            newValue = '';
          }
        }
        if (this.otherValue) {
          if (this.isArrayType) {
            if (newValue.includes(this.otherValue)) {
              this.selectedModel.push(this.otherValue);
              this.otherValue = '';
            } else if (newValue == this.otherValue) {
              this.selectedModel = this.otherValue;
              this.otherValue = '';
            }
          } else {
            this.otherValue = '';
          }
        }
        this.selectedModel = newValue;
      },
    },
  },
  methods: {
    ensureOtherIsUnique() {
      if (this.otherConflictsExistingOptions) {
        this.otherValue = '';
      }
    },
    onOtherSelected(toggle) {
      this.ensureOtherIsUnique();
      this.onToggle(toggle);
    },
    onToggle(toggle) {
      if (this.readonly) return;
      toggle();
    },
    isComplete(active, stringValue) {
      if (active && this.readonly) {
        return true;
      }
      if (this.isArrayType && this.value) {
        return this.value.includes(stringValue);
      }
      return this.value === stringValue;
    },
    confirmSelection() {
      this.$emit('input', this.selection);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.reviewable-extraction-field::before,
.reviewable-extraction-field::after {
  display: none;
}
.reviewable-extraction-field.v-expansion-panel--active
  > .v-expansion-panel-header {
  min-height: 0px !important;
}

.divider {
  border-color: map-get($grey, 'lighten-3') !important;
}
</style>

<style lang="scss">
.reviewable-extraction-field__content
  .v-expansion-panel-content__wrap {
  padding: 0px !important;
}
.reviewable-extraction-field .v-expansion-panel-header {
  min-height: unset !important;
}

.reviewable-extraction-field__confirm-row {
  height: 48px;
}
</style>
